import React, {
  MouseEvent,
  ReactNode,
  useMemo,
  useState,
} from 'react';
import {
  PatientMediaCategoryDTO,
} from 'dtos';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import Menu, {
  MenuProps,
} from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import clsx from 'clsx';
import isNil from 'lodash/isNil';
import map from 'lodash/map';
import filter from 'lodash/filter';
import {
  useGetMediaCategories,
} from 'pages/Dashboard/pages/Charts/pages/Details/components/hooks';

const getSubmenuPosition = (subMenuAnchorEl: HTMLElement | null) => {
  const shouldOpenLeft = !isNil(subMenuAnchorEl)
    && subMenuAnchorEl.getBoundingClientRect().right + 200 > window.innerWidth - 16;

  return {
    anchorOrigin: {
      vertical: 'top' as const,
      horizontal: shouldOpenLeft ? ('left' as const) : ('right' as const),
    },
    transformOrigin: {
      vertical: 'top' as const,
      horizontal: shouldOpenLeft ? ('right' as const) : ('left' as const),
    },
  };
};

export type Category = {
  categoryName: string;
  mediaType: string;
};

type Props = Pick<MenuProps, 'anchorOrigin' | 'transformOrigin'> & {
  value: string;
  anchorEl: null | HTMLElement;
  onClose: () => void;
  onChange: (value: Category) => void;
  title?: ReactNode;
};

export default function MediaTypeMenu({
  value,
  onChange,
  anchorEl,
  onClose,
  title,
  ...props
}: Props) {
  const [subMenuAnchorEl, setSubMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedCategory, setSelectedCategory] = useState<PatientMediaCategoryDTO | null>(null);
  const open = Boolean(anchorEl);
  const subMenuOpen = Boolean(subMenuAnchorEl);

  const handleMenuClose = () => {
    setSubMenuAnchorEl(null);
    setSelectedCategory(null);
    onClose();
  };

  const handleSubMenuClose = () => {
    setSubMenuAnchorEl(null);
    setSelectedCategory(null);
  };

  const handleCategoryHover = (
    event: MouseEvent<HTMLElement>,
    category: PatientMediaCategoryDTO,
  ) => {
    const target = event.currentTarget;
    // Always update the selected category and anchor
    setSelectedCategory(category);
    setSubMenuAnchorEl(target);
  };

  const submenuPosition = getSubmenuPosition(subMenuAnchorEl);

  const onMediaTypeChange = async (event: MouseEvent, mediaType: string) => {
    event.stopPropagation();

    handleMenuClose();
    onChange({
      categoryName: selectedCategory?.categoryName ?? '',
      mediaType,
    });
  };

  const categories = useGetMediaCategories();

  const editableCategories = useMemo(
    () => filter(
      map(categories, (category) => ({
        ...category,
        mediaTypes: filter(
          category.mediaTypes,
          (mediaType) => mediaType.isUploadableFromChart,
        ),
      })) as PatientMediaCategoryDTO[],
      (category) => (category.mediaTypes?.length ?? 0) > 0,
    ),
    [categories],
  );

  return (
    <>
      {!isNil(anchorEl) && (
        <Menu
          anchorEl={anchorEl}
          open={open}
          onClose={handleMenuClose}
          onClick={(e) => e.stopPropagation()}
          anchorOrigin={props.anchorOrigin ?? {
            vertical: 'bottom',
            horizontal: 'right',
          }}
          transformOrigin={props.transformOrigin ?? {
            vertical: 'top',
            horizontal: 'right',
          }}
          slotProps={{
            paper: {
              sx: { minWidth: 250 },
            },
            list: {
              sx: {
                '& .MuiMenuItem-root:not([disabled])': {
                  cursor: 'pointer',
                  '&:hover': {
                    backgroundColor: 'rgba(0, 0, 0, 0.04)',
                  },
                },
              },
            },
          }}
        >
          {title}
          {editableCategories.map((category) => (
            <MenuItem
              key={category.categoryName}
              onMouseEnter={(e) => handleCategoryHover(e, category)}
              className={clsx(
                '!text-xs !flex !items-center',
                { '!bg-gray-100': selectedCategory?.categoryName === category.categoryName },
              )}
            >
              <span className="flex-1">{category.categoryName}</span>
              <ChevronRightIcon className="!w-5 !h-5" />
            </MenuItem>
          ))}
        </Menu>
      )}
      {!isNil(subMenuAnchorEl) && (
        <Menu
          key={selectedCategory?.categoryName ?? 'submenu'}
          anchorEl={subMenuAnchorEl}
          open={subMenuOpen}
          onClose={handleSubMenuClose}
          onClick={(e) => e.stopPropagation()}
          anchorOrigin={submenuPosition.anchorOrigin}
          transformOrigin={submenuPosition.transformOrigin}
          disableAutoFocus
          disableEnforceFocus
          hideBackdrop
          slotProps={{
            root: {
              sx: { pointerEvents: 'none' },
            },
            paper: {
              sx: {
                minWidth: 200,
                pointerEvents: 'auto',
              },
              onMouseLeave: handleSubMenuClose,
            },
            list: {
              sx: {
                '& .MuiMenuItem-root': {
                  cursor: 'pointer',
                  '&:hover': {
                    backgroundColor: 'rgba(0, 0, 0, 0.04)',
                  },
                },
              },
            },
          }}
        >
          {selectedCategory?.mediaTypes?.map((mediaType) => {
            const isCurrentType = value === (mediaType.value || mediaType.label);
            return (
              <MenuItem
                key={mediaType.value}
                onClick={(e) => onMediaTypeChange(e, mediaType.value || mediaType.label || '')}
                disabled={isCurrentType}
                className={clsx('!text-xs !text-gray-600', { '!opacity-60 cursor-not-allowed': isCurrentType })}
              >
                {mediaType.label}
              </MenuItem>
            );
          })}
        </Menu>
      )}
    </>
  );
}
